import {ParseBlockedUsersCookiesError} from '../Errors/ParseBlockedUsersCookiesError.js'
import {BlockedUsersCookiesValueObject} from '../ValueObjects/BlockedUsersCookiesValueObject.js'
import UserRepository from './UserRepository.js'

/**
 * @class
 * @implements UserRepository
 */
export default class CookieUserMotosRepository extends UserRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Function} deps.jsCookie
   * @param {Config} deps.config
   * @param {userEntityFactory} deps.userEntityFactory
   * @param {Function} deps.log
   */
  constructor({config, userEntityFactory, log, jsCookie}) {
    super({
      config,
      userEntityFactory,
      log,
      jsCookie
    })
    this._config = config
    this._userEntityFactory = userEntityFactory
    this._log = log
    this._jsCookie = jsCookie
    this._LOGIN_COOKIE_NAME = this._config.get('login').LOGIN_COOKIE_NAME
    this._LEGACY_LOGIN_COOKIE_NAME = this._config.get('login').LEGACY_LOGIN_COOKIE_NAME
    this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS = this._config.get('login').LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS
    this._SECURE_COOKIES = this._config.get('SECURE_COOKIES')
    this._BLOCKED_USERS_COOKIE = this._config.get('COMMON').COOKIES.BLOCKED_USERS_KEY
  }

  /**
   * @return {UserEntity}
   */
  getCurrentUser() {
    const cookie = this._config.get('cookie')
    let motosUserData
    try {
      motosUserData = cookie && cookie.MotosUserData && JSON.parse(cookie.MotosUserData)
    } catch (e) {}

    return Promise.resolve(
      motosUserData &&
        this._userEntityFactory({
          lastName: motosUserData.lastName,
          name: motosUserData.name,
          email: motosUserData.email,
          phone: motosUserData.phone,
          userId: motosUserData.userid,
          avatar: motosUserData.avatar,
          token: motosUserData.token,
          province: motosUserData.province,
          provinceId: motosUserData.provinceID ? String(motosUserData.provinceID) : null,
          zipCode: motosUserData.zipCode,
          hidePhone: motosUserData.hidePhone
        })
    )
  }

  _cleanUndefinedProperties(object) {
    return JSON.parse(JSON.stringify(object))
  }

  _getUserCookies() {
    return this._jsCookie.get('MotosUserData')
  }

  _getUserData(cookie) {
    return cookie && cookie.MotosUserData && JSON.parse(cookie.MotosUserData)
  }

  _updateUserData({cookie, newUserData}) {
    const cookieUserData = this._getUserData(cookie)
    const cleanNewUserData = this._cleanUndefinedProperties(newUserData)
    const params = {...cookieUserData, ...cleanNewUserData}

    cookie[this._LOGIN_COOKIE_NAME] = JSON.stringify(params)
    this._jsCookie.set(this._LOGIN_COOKIE_NAME, params, {
      expires: this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS,
      secure: this._SECURE_COOKIES
    })
  }

  hasCookie(cookieName) {
    return Boolean(this._jsCookie.get(cookieName))
  }

  /**
   * @param {Request} updateCurrentUserRequest
   */
  updateCurrentUser({shouldReadUpdatedVO, updateCurrentUserRequest}) {
    const cookie = this._config.get('cookie')
    const newUserData = updateCurrentUserRequest.toJSON()
    this._updateUserData({cookie, newUserData: shouldReadUpdatedVO ? newUserData.userData : newUserData})

    return this.getCurrentUser()
  }

  /**
   * @param {Object} deps
   * @param {string} deps.name
   * @param {string} deps.email
   * @param {string} deps.phone
   * @param {string} deps.userId
   * @param {string} deps.avatar
   * @param {string} deps.token
   * @param {string} deps.province
   */
  updateUser({name, email, phone, userId, avatar, token, province}) {
    const userData = {name, email, phone, userId, avatar, token, province}
    const newData = Object.keys(userData)
      .filter(key => typeof userData[key] !== 'undefined')
      .reduce((newData, key) => ({...newData, [key]: userData[key]}), {})
    const cookieUserData = this._getUserCookies() && JSON.parse(this._getUserCookies())
    const updatedUserData = {...cookieUserData, ...newData}
    const domainCookie = this._config.get('cookie')
    this._config.set('cookie', {
      ...domainCookie,
      [this._LOGIN_COOKIE_NAME]: JSON.stringify(updatedUserData)
    })
    this._jsCookie.set(this._LOGIN_COOKIE_NAME, updatedUserData, {
      expires: this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS,
      secure: this._SECURE_COOKIES
    })
  }

  setAuthenticationToken({authenticationToken, userId}) {
    this._jsCookie.set(`authenticationToken_${userId}`, authenticationToken, {
      expires: this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS,
      secure: this._SECURE_COOKIES
    })
    return Promise.resolve()
  }

  setToken({token}) {
    const motosUserData = this._getUserCookies() ? JSON.parse(this._getUserCookies()) : {}
    this._jsCookie.set(
      this._LOGIN_COOKIE_NAME,
      {...motosUserData, token},
      {
        expires: this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS,
        secure: this._SECURE_COOKIES
      }
    )
    return Promise.resolve()
  }

  getBlockedUsers() {
    try {
      let blockedCookieValue = []
      const cookieValue = this._jsCookie.get(this._BLOCKED_USERS_COOKIE)
      if (cookieValue === undefined || cookieValue === null || cookieValue === '' || cookieValue === 'undefined') {
        return BlockedUsersCookiesValueObject.createFromCookie({blockedUsersParsedCookie: blockedCookieValue})
      }

      if (typeof cookieValue === 'string' && cookieValue.trim() === '') {
        return BlockedUsersCookiesValueObject.createFromCookie({blockedUsersParsedCookie: blockedCookieValue})
      }

      const parsedCookie = JSON.parse(cookieValue)

      if (Array.isArray(parsedCookie)) {
        blockedCookieValue = parsedCookie
        return BlockedUsersCookiesValueObject.createFromCookie({blockedUsersParsedCookie: blockedCookieValue})
      }

      return BlockedUsersCookiesValueObject.createFromCookie({blockedUsersParsedCookie: blockedCookieValue})
    } catch (error) {
      throw ParseBlockedUsersCookiesError.create({
        message: `Failed to parse blocked users cookies: ${error.message}`
      })
    }
  }
}
